import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Check } from "lucide-react";
import { useNotifications, useMarkNotificationRead } from "../hooks";
import type { Notification } from "../types";

interface Props {
  onClose: () => void;
}

export function NotificationList({ onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: notifications = [], isLoading } = useNotifications(20);
  const markRead = useMarkNotificationRead();

  const handleMarkAllRead = useCallback(async () => {
    await markRead.mutateAsync(undefined);
  }, [markRead]);

  const handleClick = useCallback(
    async (notification: Notification) => {
      if (!notification.read) {
        await markRead.mutateAsync(notification.id);
      }
      // Invalidate relevant queries so the destination page shows fresh data
      if (
        notification.type === "borrow_request" ||
        notification.type === "borrow_approved" ||
        notification.type === "borrow_denied"
      ) {
        void queryClient.invalidateQueries({ queryKey: ["borrowRequests"] });
      }
      if (notification.type === "neighbor_request" || notification.type === "neighbor_accepted") {
        void queryClient.invalidateQueries({ queryKey: ["neighborRequests"] });
        void queryClient.invalidateQueries({ queryKey: ["neighbors"] });
      }
      onClose();
      // Navigate based on type
      if (notification.type === "borrow_approved" || notification.type === "borrow_denied") {
        void navigate("/assistant");
      } else if (notification.type === "borrow_request") {
        void navigate("/neighbors?tab=requests");
      } else {
        void navigate("/neighbors");
      }
    },
    [markRead, navigate, onClose, queryClient],
  );

  if (isLoading) {
    return (
      <div className="p-3 text-center">
        <p className="text-muted-foreground text-xs">Loading...</p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center p-4 text-center">
        <Check className="text-muted-foreground/50 mb-1 h-5 w-5" />
        <p className="text-muted-foreground text-xs font-medium">All caught up</p>
        <p className="text-muted-foreground mt-0.5 text-[10px]">You have no new notifications.</p>
      </div>
    );
  }

  return (
    <div className="max-h-80 overflow-y-auto">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-medium">Notifications</span>
        <button
          type="button"
          onClick={() => void handleMarkAllRead()}
          className="text-muted-foreground hover:text-foreground text-[10px]"
        >
          Mark all read
        </button>
      </div>
      {notifications.map((n) => (
        <button
          key={n.id}
          type="button"
          onClick={() => void handleClick(n)}
          className={`hover:bg-muted w-full border-b px-3 py-2 text-left last:border-0 ${
            n.read ? "opacity-60" : ""
          }`}
        >
          <p className="text-xs font-medium">{n.title}</p>
          <p className="text-muted-foreground mt-0.5 text-[10px]">{n.message}</p>
        </button>
      ))}
    </div>
  );
}
